/**
 * pomodoro-store — timer + task list for the Pomodoro overlay.
 *
 * Persisted to localStorage (`kos.pomodoro.v1`) so the timer survives closing
 * the overlay and reloading the page; nothing is server-side. The timer maths
 * lives in `pomodoro-timer.ts`; this store just applies it to `Date.now()`.
 */

import { useEffect, useState } from 'react';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import {
  formatClock,
  initialTimer,
  pause,
  remainingAt,
  reset,
  start,
  switchMode,
  type PomodoroMode,
  type TimerState,
} from './pomodoro-timer';

interface PomodoroStore extends TimerState {
  /** Tasks being worked on, in insertion order. */
  taskIds: string[];
  focusTaskId: string | null;
  start: () => void;
  pause: () => void;
  reset: () => void;
  switchMode: (mode: PomodoroMode) => void;
  /** Called by the finish watcher: back to idle on the same mode. */
  finish: () => void;
  /** Replace the list (Add dialog result); clears focus if it was dropped. */
  setTasks: (ids: string[]) => void;
  setFocus: (id: string | null) => void;
  /** Drop a task that no longer exists (404). */
  removeTask: (id: string) => void;
}

function timerOf(s: PomodoroStore): TimerState {
  return { mode: s.mode, status: s.status, endsAt: s.endsAt, remainingMs: s.remainingMs };
}

export const POMODORO_STORAGE_KEY = 'kos.pomodoro.v1';

export const usePomodoroStore = create<PomodoroStore>()(
  persist(
    (set, get) => ({
      ...initialTimer(),
      taskIds: [],
      focusTaskId: null,
      start: () => set(start(timerOf(get()), Date.now())),
      pause: () => set(pause(timerOf(get()), Date.now())),
      reset: () => set(reset(timerOf(get()))),
      switchMode: (mode) => set(switchMode(timerOf(get()), mode)),
      finish: () => set(reset(timerOf(get()))),
      setTasks: (ids) => {
        const unique = [...new Set(ids)];
        const { focusTaskId } = get();
        set({
          taskIds: unique,
          focusTaskId: focusTaskId && unique.includes(focusTaskId) ? focusTaskId : null,
        });
      },
      setFocus: (id) => set({ focusTaskId: id }),
      removeTask: (id) => {
        const { taskIds, focusTaskId } = get();
        if (!taskIds.includes(id)) return;
        set({
          taskIds: taskIds.filter((t) => t !== id),
          focusTaskId: focusTaskId === id ? null : focusTaskId,
        });
      },
    }),
    {
      name: POMODORO_STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      // Persist data only — actions are re-created on load.
      partialize: (s) => ({
        mode: s.mode,
        status: s.status,
        endsAt: s.endsAt,
        remainingMs: s.remainingMs,
        taskIds: s.taskIds,
        focusTaskId: s.focusTaskId,
      }),
    },
  ),
);

// Keep every open tab on the same timer: another tab's start/pause/finish
// arrives as a storage event, so this tab never shows or finishes a stale run.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    if (e.key === POMODORO_STORAGE_KEY) void usePomodoroStore.persist.rehydrate();
  });
}

/**
 * Live remaining time. Re-renders every 250ms while the timer is running so
 * the displayed second flips promptly; idle/paused don't tick. Keep this in a
 * small leaf component so the tick doesn't re-render a whole page.
 */
export function usePomodoroClock(): { remainingMs: number; label: string } {
  const mode = usePomodoroStore((s) => s.mode);
  const status = usePomodoroStore((s) => s.status);
  const endsAt = usePomodoroStore((s) => s.endsAt);
  const storedRemaining = usePomodoroStore((s) => s.remainingMs);
  // The tick only forces a re-render; time is read during render so the
  // first paint after Resume isn't computed against a stale `now`.
  const [, setTick] = useState(0);

  useEffect(() => {
    if (status !== 'running') return;
    const id = window.setInterval(() => setTick((t) => t + 1), 250);
    return () => window.clearInterval(id);
  }, [status, endsAt]);

  const remainingMs = remainingAt(
    { mode, status, endsAt, remainingMs: storedRemaining },
    Date.now(),
  );
  return { remainingMs, label: formatClock(remainingMs) };
}
